import { createSlice } from "@reduxjs/toolkit";
import { DTLayer } from "src/services/data-layer";

// Import types
import { PaStatus, WsStatusTypes, PauseType } from "src/types";

export enum AppStatus {
  LOADING,
  IDLE,
  RINGING,
  IN_CALL,
  IN_PAUSE,
}

type ContextState = {
  appStatus: AppStatus;
  wsStatus: WsStatusTypes;

  // Sidebars
  sidebar: null | string;
  sidebarCollapsed: boolean;

  // Variables
  availablePas: Array<PaStatus>;
  availablePauseTypes: Array<PauseType>;
  globalTimer: number;
  lastStatusUpdate: number | null;
};

const initialState: ContextState = {
  // Status
  appStatus: AppStatus.LOADING,
  wsStatus: WsStatusTypes.DISCONNECTED,

  // Sidebar
  sidebarCollapsed: false,
  sidebar: null,

  // Variables
  availablePas: [],
  availablePauseTypes: [],
  globalTimer: new Date().getTime(),
  lastStatusUpdate: null,
};

const contextSlice = createSlice({
  name: "context",
  initialState,
  reducers: {
    // => Status contollers
    appLoaded: (state) => {
      state.appStatus = AppStatus.IDLE;
    },
    setIdle: (state) => {
      state.appStatus = AppStatus.IDLE;
    },
    setLoading: (state) => {
      state.appStatus = AppStatus.LOADING;
    },
    togglePause: (state, { payload }) => {
      if (payload) {
        state.appStatus = AppStatus.IN_PAUSE;
      } else {
        state.appStatus = AppStatus.IDLE;
        state.lastStatusUpdate = null;
      }

      DTLayer.dispatch("togglePause", {});
    },
    timerTick: (state) => {
      state.globalTimer = new Date().getTime();
    },
    pauseTypesLoaded: (state, { payload }) => {
      state.availablePauseTypes = payload;
    },

    // => Available PAs
    updateAvailablePas: (state, { payload }) => {
      let pos = -1;

      const foundPa = state.availablePas.find((p, index) => {
        if (p.paQueueId === payload.paQueueId) {
          pos = index;
          return p;
        }
      });

      // If PA already exists update
      if (foundPa) {
        if (payload.status === "Disconnected") {
          state.availablePas.splice(pos, 1);
        } else {
          foundPa.status = payload.status;
        }
      } else {
        state.availablePas.push(payload);
      }
    },
    updateLastStatusDate: (state, { payload }: { payload: number | null }) => {
      state.lastStatusUpdate = payload;
    },
    loadPasStatus: (state, { payload }) => {
      payload.pasStatus.forEach((pa: any) => state.availablePas.push(pa));
    },

    // => Call Actions
    startRinging: (state) => {
      state.appStatus = AppStatus.RINGING;
    },
    stopRinging: (state) => {
      state.appStatus = AppStatus.IDLE;
    },
    inCall: (state) => {
      state.appStatus = AppStatus.IN_CALL;
    },

    // => Websockets
    wsConnected: (state) => {
      state.wsStatus = WsStatusTypes.CONNECTED;
    },
    wsDisconnected: (state) => {
      state.wsStatus = WsStatusTypes.DISCONNECTED;
    },

    // => UI Controllers
    toggleSidebar: (state, { payload }) => {
      state.sidebar = payload;

      DTLayer.dispatch("toggleAside", {});
    },
    collapseSide: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
  },
});

export const {
  // App Events
  appLoaded,
  wsConnected,
  togglePause,
  wsDisconnected,
  toggleSidebar,
  setLoading,
  collapseSide,
  updateAvailablePas,
  updateLastStatusDate,
  loadPasStatus,
  setIdle,
  timerTick,
  pauseTypesLoaded,

  // Call Events
  startRinging,
  stopRinging,
  inCall,
} = contextSlice.actions;
export default contextSlice.reducer;
